import type { ReactNode } from "react";

import type { LucideIcon } from "lucide-react";

type EngagementEmptyPanelProps = {
  title: string;
  hint?: ReactNode;
  icon?: LucideIcon;
  variant?: "hub-page" | "hub-compact" | "sponsor";
  className?: string;
  action?: ReactNode;
};

/** Empty states aligned with Assessment Hub and My Assessments patterns. */
const EngagementEmptyPanel = ({
  title,
  hint,
  icon: Icon,
  variant = "sponsor",
  className = "",
  action,
}: EngagementEmptyPanelProps) => {
  if (variant === "hub-page") {
    return (
      <div
        className={`flex flex-col items-center justify-center min-h-[400px] py-12 text-center ${className}`}
      >
        {Icon ? (
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
            <Icon className="h-8 w-8 text-gray-400" aria-hidden />
          </div>
        ) : null}
        <p className="mb-0 text-base font-medium text-gray-600">{title}</p>
        {hint ? (
          <p className="mb-0 mt-2 text-sm text-gray-500">{hint}</p>
        ) : null}
        {action ? <div className="mt-4">{action}</div> : null}
      </div>
    );
  }

  if (variant === "hub-compact") {
    return (
      <div
        className={`rounded-2xl border border-dashed border-gray-300 bg-white px-8 py-12 text-center ${className}`}
      >
        {Icon ? (
          <div className="mx-auto mb-3 inline-flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
            <Icon className="h-6 w-6 text-gray-500" aria-hidden />
          </div>
        ) : null}
        <p className="mb-1 text-base font-semibold text-gray-900">{title}</p>
        {hint ? <p className="mb-0 text-sm text-gray-600">{hint}</p> : null}
        {action ? <div className="mt-4">{action}</div> : null}
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl border border-dashed border-gray-300 bg-white px-16 py-20 text-center ${className}`}
    >
      {Icon ? (
        <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
          <Icon className="h-6 w-6 text-gray-500" aria-hidden />
        </div>
      ) : null}
      <p className="mb-1 text-base font-semibold text-gray-900">{title}</p>
      {hint ? <p className="mb-0 text-sm text-gray-600">{hint}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
};

type HubPageLoadingProps = {
  message?: string;
  compact?: boolean;
  className?: string;
};

export const HubPageLoading = ({
  message = "Loading…",
  compact = false,
  className = "",
}: HubPageLoadingProps) => (
  <div
    className={`flex flex-col items-center justify-center bg-white rounded-lg border border-gray-200 ${
      compact ? "py-16" : "min-h-[280px] py-12"
    } ${className}`}
  >
    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-purple-600" />
    <p className="mb-0 mt-3 text-sm text-gray-600">{message}</p>
  </div>
);

/** Fill a fixed-height modal body without an extra bordered card. */
export const hubModalLoadingClassName =
  "h-full min-h-0 flex-1 w-full border-0 rounded-none bg-transparent";

export default EngagementEmptyPanel;
